// ─────────────────────────────────────────────────────────────────
// Glass-i — TDS / TCS helpers (Stage 6P.9).
//
// Pure ESM module — exercised by the frontend node:test runner.
//
// Frontend mirrors of the backend TDS / TCS enums + label / colour
// vocabulary used by the Stage 6P.9 register / challan / report pages.
// The backend
// (`backend/src/services/erp/tdsTcs/*`
//  — plus the shared accounting `voucherPosting.service.js`)
// remains the source of truth for ALL TDS / TCS math + lifecycle:
//   - deduction_no / challan_no allocation
//     (TDS-/TCS-/TDSCH-YYYYMMDD-NNNNNN) via advisory locks,
//   - tds_tcs_rules row selection by tax_type + section_code + date
//     window (effective_from <= date <= effective_to),
//   - PAN-aware rate ladder (higher_rate_no_pan_percent kicks in when
//     the supplier PAN is missing/invalid),
//   - lower-deduction certificate amount-used tracking,
//   - threshold register cumulative_base_amount vs threshold_amount
//     bookkeeping,
//   - voucher posting via the shared voucherPosting public contract,
//   - reversal-on-purchase-return (register-only, no voucher cancel),
//   - challan PAID lifecycle.
//
// These helpers exist purely so the operator-facing list / drawer /
// report surfaces stay typed + colour-consistent with the rest of the
// control plane chrome. They never recompute math — the backend reply
// is the only source the UI trusts.
//
// Enum lists are sealed with Object.freeze so callers can't mutate
// the shared lists by accident.
//
// Style references (one per surface area):
//   - frontend/src/pages/erp/purchaseReturns/purchaseReturnHelpers.js
//   - frontend/src/pages/erp/supplierPayments/supplierPaymentHelpers.js
// ─────────────────────────────────────────────────────────────────


// ─────────────────────────────────────────────────────────────────
// Enum mirrors — kept aligned with backend
// `services/erp/tdsTcs/tdsTcs.constants.js`. Frozen so callers can't
// mutate the shared lists by accident.
// ─────────────────────────────────────────────────────────────────

export const TAX_TYPES = Object.freeze(['TDS', 'TCS']);

// Suggestion list ONLY — runtime accepts any string the operator
// types into the rule editor.
export const SECTION_CODES_KNOWN = Object.freeze([
  '194Q',
  '194C',
  '194J',
  '194H',
  '194I',
  '206C_LEGACY'
]);

export const SOURCE_TYPES = Object.freeze([
  'PURCHASE_BILL',
  'SUPPLIER_PAYMENT',
  'PURCHASE_RETURN',
  'MANUAL'
]);

// Mirrors of backend tdsTcs.constants.js — used by rule/profile forms
// and the validation helpers below. Frozen so callers can't mutate
// the shared lists by accident.

export const APPLIES_TO = Object.freeze([
  'GOODS',
  'SERVICE',
  'JOBWORK',
  'RENT',
  'COMMISSION',
  'OTHER'
]);

export const RESIDENT_TYPES = Object.freeze([
  'RESIDENT',
  'NON_RESIDENT',
  'ANY'
]);

export const THRESHOLD_BASIS = Object.freeze([
  'PER_SUPPLIER_FY',
  'PER_TRANSACTION',
  'NONE'
]);

export const DEDUCTION_TIMINGS = Object.freeze([
  'BILL_ACCOUNTING',
  'PAYMENT',
  'EARLIER_OF_BILL_OR_PAYMENT'
]);

export const PAN_STATUSES = Object.freeze([
  'AVAILABLE',
  'NOT_AVAILABLE',
  'INVALID'
]);

export const SUPPLIER_TAX_TYPES = Object.freeze([
  'INDIVIDUAL',
  'COMPANY',
  'PARTNERSHIP',
  'PROPRIETOR',
  'BUSINESS',
  'OTHER'
]);

export const RESIDENT_STATUSES = Object.freeze([
  'RESIDENT',
  'NON_RESIDENT',
  'UNKNOWN'
]);

export const EVENT_TYPES = Object.freeze([
  'RULE_CREATED',
  'RULE_UPDATED',
  'PROFILE_UPDATED',
  'DEDUCTION_CREATED',
  'DEDUCTION_POSTED',
  'DEDUCTION_REVERSED',
  'CHALLAN_CREATED',
  'CHALLAN_PAID',
  'CANCELLED',
  'ERROR'
]);

export const DEDUCTION_STATUSES = Object.freeze([
  'DRAFT',
  'DEDUCTED',
  'REVERSED',
  'CANCELLED',
  'POSTING_FAILED'
]);

export const CHALLAN_STATUSES = Object.freeze([
  'NOT_PAID',
  'PARTIALLY_PAID',
  'PAID'
]);

export const CERTIFICATE_STATUSES = Object.freeze([
  'PENDING',
  'GENERATED',
  'ISSUED'
]);

export const CHALLAN_HEADER_STATUSES = Object.freeze([
  'DRAFT',
  'PAID',
  'CANCELLED'
]);


// ─────────────────────────────────────────────────────────────────
// Permission keys (mirrors the 13 RBAC strings on the backend).
// ─────────────────────────────────────────────────────────────────

export const PERMS = Object.freeze({
  RULES_VIEW:         'erp.tds_tcs.rules.view',
  RULES_CREATE:       'erp.tds_tcs.rules.create',
  RULES_UPDATE:       'erp.tds_tcs.rules.update',
  RULES_DELETE:       'erp.tds_tcs.rules.delete',
  PROFILES_VIEW:      'erp.tds_tcs.profiles.view',
  PROFILES_UPDATE:    'erp.tds_tcs.profiles.update',
  DEDUCTIONS_VIEW:    'erp.tds_tcs.deductions.view',
  DEDUCTIONS_CREATE:  'erp.tds_tcs.deductions.create',
  DEDUCTIONS_CANCEL:  'erp.tds_tcs.deductions.cancel',
  CHALLANS_VIEW:      'erp.tds_tcs.challans.view',
  CHALLANS_CREATE:    'erp.tds_tcs.challans.create',
  CHALLANS_MARK_PAID: 'erp.tds_tcs.challans.mark_paid',
  REPORTS_VIEW:       'erp.tds_tcs.reports.view'
});


// ─────────────────────────────────────────────────────────────────
// Tolerant numeric coercion — mirrors backend toNumber.
// Null / undefined / empty string / non-finite all collapse to 0.
// Never throws, never returns NaN.
// ─────────────────────────────────────────────────────────────────

export function toNumber(value) {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return 0;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : 0;
  }
  if (typeof value === 'boolean') return value ? 1 : 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}


// ─────────────────────────────────────────────────────────────────
// Snake / camel access — register rows arrive in camel from
// services/erp/tdsTcs/tdsTcs.mapper.js, but anything replayed from a
// local draft or a fixture may carry either casing. Probe both and
// never mutate the caller.
// ─────────────────────────────────────────────────────────────────

export function pickField(row, snake, camel) {
  if (!row || typeof row !== 'object') return undefined;
  if (camel in row && row[camel] !== undefined && row[camel] !== null) return row[camel];
  if (snake in row && row[snake] !== undefined && row[snake] !== null) return row[snake];
  return undefined;
}


// ─────────────────────────────────────────────────────────────────
// Formatters
// ─────────────────────────────────────────────────────────────────

export function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const day   = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleString('en-US', { month: 'short' });
  const year  = d.getFullYear();
  return `${day}-${month}-${year}`;
}

export function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const day   = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleString('en-US', { month: 'short' });
  const year  = d.getFullYear();
  const hh    = String(d.getHours()).padStart(2, '0');
  const mm    = String(d.getMinutes()).padStart(2, '0');
  return `${day}-${month}-${year} ${hh}:${mm}`;
}

// ₹ grouping uses en-IN so 12,34,567.89 lines up with the rest of the
// control plane. Falsy / non-finite inputs collapse to an em-dash so
// empty rows don't read as "₹0".
export function formatMoney(value) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  try {
    return n.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  } catch {
    return n.toFixed(2);
  }
}

// Percent — formats to 4dp (matches tds_tcs_rules.rate_percent
// NUMERIC(7,4)) but trims trailing zeros for readability.
export function formatPercent(value) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  const fixed = n.toFixed(4);
  // Trim trailing zeros + lone trailing dot.
  return `${fixed.replace(/\.?0+$/, '')}%`;
}


// ─────────────────────────────────────────────────────────────────
// Status label + colour vocabulary. Colours align with the AntD Tag
// palette guidance in docs/23-antd-component-standards.md.
// ─────────────────────────────────────────────────────────────────

function normalizeKey(value) {
  if (!value) return '';
  return String(value).trim().toUpperCase();
}

// Tax type — TDS vs TCS at-a-glance distinction.
const TAX_TYPE_LABELS = {
  TDS: 'TDS',
  TCS: 'TCS'
};

const TAX_TYPE_COLORS = {
  TDS: 'geekblue',
  TCS: 'purple'
};

export function taxTypeLabel(value) {
  const key = normalizeKey(value);
  return TAX_TYPE_LABELS[key] || key || '';
}

export function taxTypeColor(value) {
  const key = normalizeKey(value);
  return TAX_TYPE_COLORS[key] || 'default';
}


// Deduction status (tds_tcs_deductions.status).
const DEDUCTION_STATUS_LABELS = {
  DRAFT:          'Draft',
  DEDUCTED:       'Deducted',
  REVERSED:       'Reversed',
  CANCELLED:      'Cancelled',
  POSTING_FAILED: 'Posting Failed'
};

const DEDUCTION_STATUS_COLORS = {
  DRAFT:          'default',
  DEDUCTED:       'success',
  REVERSED:       'orange',
  CANCELLED:      'default',
  POSTING_FAILED: 'error'
};

export function deductionStatusLabel(value) {
  const key = normalizeKey(value);
  return DEDUCTION_STATUS_LABELS[key] || key || '';
}

export function deductionStatusColor(value) {
  const key = normalizeKey(value);
  return DEDUCTION_STATUS_COLORS[key] || 'default';
}


// Per-deduction challan settlement axis (tds_tcs_deductions.challan_status).
const CHALLAN_STATUS_LABELS = {
  NOT_PAID:       'Not Paid',
  PARTIALLY_PAID: 'Partially Paid',
  PAID:           'Paid'
};

const CHALLAN_STATUS_COLORS = {
  NOT_PAID:       'default',
  PARTIALLY_PAID: 'processing',
  PAID:           'success'
};

export function challanStatusLabel(value) {
  const key = normalizeKey(value);
  return CHALLAN_STATUS_LABELS[key] || key || '';
}

export function challanStatusColor(value) {
  const key = normalizeKey(value);
  return CHALLAN_STATUS_COLORS[key] || 'default';
}


// Per-deduction Form 16A / 27D certificate axis
// (tds_tcs_deductions.certificate_status).
const CERTIFICATE_STATUS_LABELS = {
  PENDING:   'Pending',
  GENERATED: 'Generated',
  ISSUED:    'Issued'
};

const CERTIFICATE_STATUS_COLORS = {
  PENDING:   'default',
  GENERATED: 'processing',
  ISSUED:    'success'
};

export function certificateStatusLabel(value) {
  const key = normalizeKey(value);
  return CERTIFICATE_STATUS_LABELS[key] || key || '';
}

export function certificateStatusColor(value) {
  const key = normalizeKey(value);
  return CERTIFICATE_STATUS_COLORS[key] || 'default';
}


// Challan header lifecycle (tds_tcs_challans.status).
const CHALLAN_HEADER_LABELS = {
  DRAFT:     'Draft',
  PAID:      'Paid',
  CANCELLED: 'Cancelled'
};

const CHALLAN_HEADER_COLORS = {
  DRAFT:     'default',
  PAID:      'success',
  CANCELLED: 'default'
};

export function challanHeaderStatusLabel(value) {
  const key = normalizeKey(value);
  return CHALLAN_HEADER_LABELS[key] || key || '';
}

export function challanHeaderStatusColor(value) {
  const key = normalizeKey(value);
  return CHALLAN_HEADER_COLORS[key] || 'default';
}


// Source-type tag — purchase bill / supplier payment / purchase
// return / manual. Drives the "Source Type" column on the deduction
// register.
const SOURCE_TYPE_LABELS = {
  PURCHASE_BILL:    'Purchase Bill',
  SUPPLIER_PAYMENT: 'Supplier Payment',
  PURCHASE_RETURN:  'Purchase Return',
  MANUAL:           'Manual'
};

export function sourceTypeLabel(value) {
  const key = normalizeKey(value);
  return SOURCE_TYPE_LABELS[key] || (key ? key.replace(/_/g, ' ') : '');
}


// ─────────────────────────────────────────────────────────────────
// Capability gates — drive the row-actions menu in the deduction
// register. The backend remains the authority; these guards hide or
// disable CTAs for operators who clearly can't succeed.
//
//   Cancel deduction:  only DRAFT (matches brief §L3 row action gate).
//   Mark challan paid: only DRAFT (challan header).
// ─────────────────────────────────────────────────────────────────

export function canCancelDeduction(status) {
  return normalizeKey(status) === 'DRAFT';
}

export function canMarkChallanPaid(status) {
  return normalizeKey(status) === 'DRAFT';
}

export function canAllocateChallan(status) {
  return normalizeKey(status) === 'DRAFT';
}


// ─────────────────────────────────────────────────────────────────
// Financial-year helper — returns the FY string for a date in the
// "FY-2025-26" / "FY-2025" shape the backend stores on
// tds_tcs_deductions.financial_year. Operators rarely type this by
// hand; the form defaults to the current FY.
//
// India's FY runs April → March: an April-2025 date is FY 2025-26.
// ─────────────────────────────────────────────────────────────────

export function deriveFinancialYear(date) {
  const d = date instanceof Date ? date : new Date(date || Date.now());
  if (Number.isNaN(d.getTime())) return null;
  const month = d.getMonth(); // 0..11
  const year  = d.getFullYear();
  if (month >= 3) {
    // April..December → FY year-(year+1)
    return `FY-${year}-${String((year + 1) % 100).padStart(2, '0')}`;
  }
  // January..March → FY (year-1)-year
  return `FY-${year - 1}-${String(year % 100).padStart(2, '0')}`;
}


// ─────────────────────────────────────────────────────────────────
// Stage 6P.9 brief §N — minimal pure helpers used by the TDS/TCS
// surfaces. Backend is the source of truth — these are operator-side
// niceties (live recompute as the operator types, frontend validation
// mirrors so the Post button can disable instantly).
// ─────────────────────────────────────────────────────────────────

const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const FY_EPSILON = 0.005;

function round2HalfUp(value) {
  const n = toNumber(value);
  if (!Number.isFinite(n) || n === 0) return 0;
  const sign = n < 0 ? -1 : 1;
  return sign * Math.round(Math.abs(n) * 100 + 1e-9) / 100;
}

export function calculateTdsAmount(baseAmount, ratePercent) {
  const base = toNumber(baseAmount);
  const rate = toNumber(ratePercent);
  if (base <= 0 || rate <= 0) return 0;
  return round2HalfUp((base * rate) / 100);
}

export function calculateNetPaymentAmount(grossAmount, tdsAmount) {
  const gross = toNumber(grossAmount);
  const tds   = toNumber(tdsAmount);
  const net   = round2HalfUp(gross - tds);
  if (net < -FY_EPSILON) {
    const e = new Error('Net payment amount cannot be negative.');
    e.code  = 'NET_PAYMENT_NEGATIVE';
    e.details = { gross, tds, net };
    throw e;
  }
  return net === 0 ? 0 : net;
}


// ─────────────────────────────────────────────────────────────────
// formatFinancialYear — 'FY 2026-27' shape (space, not dash). The
// `deriveFinancialYear` helper above kept its legacy 'FY-2026-27'
// shape to avoid breaking callers; this one is the brief-named
// frontend mirror of the backend tdsTcsThreshold.getFinancialYear
// formatter.
// ─────────────────────────────────────────────────────────────────

export function formatFinancialYear(date) {
  if (date === null || date === undefined || date === '') return '';
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const month = d.getMonth();
  const year  = d.getFullYear();
  if (month >= 3) {
    return `FY ${year}-${String((year + 1) % 100).padStart(2, '0')}`;
  }
  return `FY ${year - 1}-${String(year % 100).padStart(2, '0')}`;
}


// ─────────────────────────────────────────────────────────────────
// sectionLabel — trimmed pass-through. Backend accepts any operator-
// typed string, so the UI just trims + surfaces.
// ─────────────────────────────────────────────────────────────────

export function sectionLabel(value) {
  if (value === null || value === undefined) return '';
  return String(value).trim();
}


// ─────────────────────────────────────────────────────────────────
// Normalisers — snake → camel + numeric widening + enum uppercasing.
// Tolerant of null / non-object input (returns null).
// ─────────────────────────────────────────────────────────────────

function isPlainObjectish(row) {
  return row !== null && row !== undefined && typeof row === 'object';
}

export function normalizeTdsTcsRule(row) {
  if (!isPlainObjectish(row)) return null;
  return {
    id:                       row.id || null,
    taxType:                  normalizeKey(pickField(row, 'tax_type',                  'taxType')),
    sectionCode:              normalizeKey(pickField(row, 'section_code',              'sectionCode')),
    description:              pickField(row, 'description',                            'description') || null,
    appliesTo:                normalizeKey(pickField(row, 'applies_to',                'appliesTo')) || 'OTHER',
    residentType:             normalizeKey(pickField(row, 'resident_type',             'residentType')) || 'RESIDENT',
    thresholdBasis:           normalizeKey(pickField(row, 'threshold_basis',           'thresholdBasis')) || 'PER_SUPPLIER_FY',
    thresholdAmount:          toNumber(pickField(row, 'threshold_amount',              'thresholdAmount')),
    perTransactionThreshold:  toNumber(pickField(row, 'per_transaction_threshold',     'perTransactionThreshold')),
    ratePercent:              toNumber(pickField(row, 'rate_percent',                  'ratePercent')),
    higherRateNoPanPercent:   toNumber(pickField(row, 'higher_rate_no_pan_percent',    'higherRateNoPanPercent')),
    deductionTiming:          normalizeKey(pickField(row, 'deduction_timing',          'deductionTiming')) || 'EARLIER_OF_BILL_OR_PAYMENT',
    payableLedgerCode:        pickField(row, 'payable_ledger_code',  'payableLedgerCode') || null,
    expenseLedgerCode:        pickField(row, 'expense_ledger_code',  'expenseLedgerCode') || null,
    effectiveFrom:            pickField(row, 'effective_from',       'effectiveFrom') || null,
    effectiveTo:              pickField(row, 'effective_to',         'effectiveTo') || null,
    isActive:                 pickField(row, 'is_active',            'isActive') !== false,
    notes:                    pickField(row, 'notes',                'notes') || null,
    createdAt:                pickField(row, 'created_at', 'createdAt') || null,
    updatedAt:                pickField(row, 'updated_at', 'updatedAt') || null
  };
}

export function normalizeSupplierTaxProfile(row) {
  if (!isPlainObjectish(row)) return null;
  return {
    id:                            row.id || null,
    supplierId:                    pickField(row, 'supplier_id',    'supplierId') || null,
    supplierName:                  pickField(row, 'supplier_name',  'supplierName') || null,
    supplierGstin:                 pickField(row, 'supplier_gstin', 'supplierGstin') || null,
    panNumber:                     pickField(row, 'pan_number',     'panNumber') || null,
    panStatus:                     normalizeKey(pickField(row, 'pan_status', 'panStatus')) || 'AVAILABLE',
    supplierTaxType:               normalizeKey(pickField(row, 'supplier_tax_type', 'supplierTaxType')) || 'OTHER',
    residentStatus:                normalizeKey(pickField(row, 'resident_status',   'residentStatus')) || 'RESIDENT',
    hasLowerDeductionCertificate:  Boolean(pickField(row, 'has_lower_deduction_certificate', 'hasLowerDeductionCertificate')),
    certificateNo:                 pickField(row, 'certificate_no',           'certificateNo') || null,
    certificateSection:            pickField(row, 'certificate_section',      'certificateSection') || null,
    certificateRatePercent:        toNumber(pickField(row, 'certificate_rate_percent', 'certificateRatePercent')),
    certificateLimitAmount:        toNumber(pickField(row, 'certificate_limit_amount', 'certificateLimitAmount')),
    certificateAmountUsed:         toNumber(pickField(row, 'certificate_amount_used',  'certificateAmountUsed')),
    certificateValidFrom:          pickField(row, 'certificate_valid_from', 'certificateValidFrom') || null,
    certificateValidTo:            pickField(row, 'certificate_valid_to',   'certificateValidTo') || null,
    isExempt:                      Boolean(pickField(row, 'is_exempt', 'isExempt')),
    exemptionReason:               pickField(row, 'exemption_reason', 'exemptionReason') || null,
    notes:                         pickField(row, 'notes', 'notes') || null
  };
}

export function normalizeTdsTcsDeduction(row) {
  if (!isPlainObjectish(row)) return null;
  return {
    id:                        row.id || null,
    deductionNo:               pickField(row, 'deduction_no',  'deductionNo') || null,
    deductionDate:             pickField(row, 'deduction_date', 'deductionDate') || null,
    financialYear:             pickField(row, 'financial_year', 'financialYear') || null,
    taxType:                   normalizeKey(pickField(row, 'tax_type', 'taxType')),
    sectionCode:               normalizeKey(pickField(row, 'section_code', 'sectionCode')),
    tdsTcsRuleId:              pickField(row, 'tds_tcs_rule_id', 'tdsTcsRuleId') || null,
    ruleRatePercent:           toNumber(pickField(row, 'rule_rate_percent', 'ruleRatePercent')),
    sourceType:                normalizeKey(pickField(row, 'source_type', 'sourceType')),
    sourceId:                  pickField(row, 'source_id', 'sourceId') || null,
    sourceDocumentNo:          pickField(row, 'source_document_no', 'sourceDocumentNo') || null,
    purchaseBillId:            pickField(row, 'purchase_bill_id', 'purchaseBillId') || null,
    purchaseBillNo:            pickField(row, 'purchase_bill_no', 'purchaseBillNo') || null,
    supplierPaymentId:         pickField(row, 'supplier_payment_id', 'supplierPaymentId') || null,
    supplierPaymentNo:         pickField(row, 'supplier_payment_no', 'supplierPaymentNo') || null,
    purchaseReturnId:          pickField(row, 'purchase_return_id', 'purchaseReturnId') || null,
    purchaseReturnNo:          pickField(row, 'purchase_return_no', 'purchaseReturnNo') || null,
    supplierId:                pickField(row, 'supplier_id', 'supplierId') || null,
    supplierName:              pickField(row, 'supplier_name', 'supplierName') || null,
    supplierGstin:             pickField(row, 'supplier_gstin', 'supplierGstin') || null,
    supplierPan:               pickField(row, 'supplier_pan', 'supplierPan') || null,
    taxableBaseAmount:         toNumber(pickField(row, 'taxable_base_amount', 'taxableBaseAmount')),
    deductionAmount:           toNumber(pickField(row, 'deduction_amount',    'deductionAmount')),
    netPayableAmount:          toNumber(pickField(row, 'net_payable_amount',  'netPayableAmount')),
    payableLedgerCode:         pickField(row, 'payable_ledger_code', 'payableLedgerCode') || null,
    accountingVoucherId:       pickField(row, 'accounting_voucher_id', 'accountingVoucherId') || null,
    accountingVoucherNo:       pickField(row, 'accounting_voucher_no', 'accountingVoucherNo') || null,
    tdsTcsChallanId:           pickField(row, 'tds_tcs_challan_id', 'tdsTcsChallanId') || null,
    challanStatus:             normalizeKey(pickField(row, 'challan_status', 'challanStatus')) || 'NOT_PAID',
    certificateStatus:         normalizeKey(pickField(row, 'certificate_status', 'certificateStatus')) || 'PENDING',
    status:                    normalizeKey(pickField(row, 'status', 'status')) || 'DRAFT',
    postedAt:                  pickField(row, 'posted_at', 'postedAt') || null,
    cancelledAt:               pickField(row, 'cancelled_at', 'cancelledAt') || null,
    reversedAt:                pickField(row, 'reversed_at', 'reversedAt') || null
  };
}

export function normalizeTdsChallan(row) {
  if (!isPlainObjectish(row)) return null;
  const rawMonth = pickField(row, 'period_month', 'periodMonth');
  const m = rawMonth === null || rawMonth === undefined || rawMonth === '' ? null : Number(rawMonth);
  const periodMonth = Number.isFinite(m) && m >= 1 && m <= 12 ? Math.trunc(m) : null;
  return {
    id:                       row.id || null,
    challanNo:                pickField(row, 'challan_no',  'challanNo') || null,
    challanDate:              pickField(row, 'challan_date', 'challanDate') || null,
    financialYear:            pickField(row, 'financial_year', 'financialYear') || null,
    periodMonth,
    taxType:                  normalizeKey(pickField(row, 'tax_type', 'taxType')),
    sectionCode:              normalizeKey(pickField(row, 'section_code', 'sectionCode')),
    totalTaxableBaseAmount:   toNumber(pickField(row, 'total_taxable_base_amount', 'totalTaxableBaseAmount')),
    totalDeductionAmount:     toNumber(pickField(row, 'total_deduction_amount',    'totalDeductionAmount')),
    totalInterestAmount:      toNumber(pickField(row, 'total_interest_amount',     'totalInterestAmount')),
    totalPenaltyAmount:       toNumber(pickField(row, 'total_penalty_amount',      'totalPenaltyAmount')),
    totalDepositedAmount:     toNumber(pickField(row, 'total_deposited_amount',    'totalDepositedAmount')),
    bsrCode:                  pickField(row, 'bsr_code', 'bsrCode') || null,
    challanSerialNo:          pickField(row, 'challan_serial_no', 'challanSerialNo') || null,
    bankReferenceNo:          pickField(row, 'bank_reference_no', 'bankReferenceNo') || null,
    paymentDate:              pickField(row, 'payment_date', 'paymentDate') || null,
    accountingVoucherId:      pickField(row, 'accounting_voucher_id', 'accountingVoucherId') || null,
    accountingVoucherNo:      pickField(row, 'accounting_voucher_no', 'accountingVoucherNo') || null,
    status:                   normalizeKey(pickField(row, 'status', 'status')) || 'DRAFT',
    remarks:                  pickField(row, 'remarks', 'remarks') || null
  };
}


// ─────────────────────────────────────────────────────────────────
// Validators — return { ok, errors: [{ path, code, message }] }
// shape (mirrors the purchaseReturnHelpers contract). Frontend
// validation is a courtesy guard; backend is the source of truth.
// ─────────────────────────────────────────────────────────────────

function pushErr(errors, path, code, message) {
  errors.push({ path, code, message });
}

export function validateTdsTcsRule(payload) {
  const errors = [];
  const p = isPlainObjectish(payload) ? payload : {};
  const taxType  = normalizeKey(p.tax_type     || p.taxType);
  const section  = normalizeKey(p.section_code || p.sectionCode);
  const rate     = toNumber(p.rate_percent !== undefined ? p.rate_percent : p.ratePercent);
  const effFrom  = p.effective_from || p.effectiveFrom || null;
  const effTo    = p.effective_to   || p.effectiveTo   || null;

  if (!taxType) {
    pushErr(errors, 'tax_type', 'TAX_TYPE_REQUIRED', 'Tax type is required.');
  } else if (!TAX_TYPES.includes(taxType)) {
    pushErr(errors, 'tax_type', 'TAX_TYPE_INVALID', 'Tax type must be TDS or TCS.');
  }
  if (!section) {
    pushErr(errors, 'section_code', 'SECTION_CODE_REQUIRED', 'Section code is required.');
  }
  if (!Number.isFinite(rate) || rate < 0) {
    pushErr(errors, 'rate_percent', 'RATE_INVALID', 'Rate must be a non-negative number.');
  } else if (rate > 100) {
    pushErr(errors, 'rate_percent', 'RATE_INVALID', 'Rate cannot exceed 100%.');
  }
  if (!effFrom) {
    pushErr(errors, 'effective_from', 'EFFECTIVE_FROM_REQUIRED', 'Effective from is required.');
  }
  if (effFrom && effTo) {
    const a = new Date(effFrom).getTime();
    const b = new Date(effTo).getTime();
    if (Number.isFinite(a) && Number.isFinite(b) && b < a) {
      pushErr(errors, 'effective_to', 'EFFECTIVE_TO_BEFORE_FROM', 'Effective-to cannot be earlier than effective-from.');
    }
  }
  return { ok: errors.length === 0, errors };
}

export function validateSupplierTaxProfile(payload) {
  const errors = [];
  const p = isPlainObjectish(payload) ? payload : {};
  const supplierId   = p.supplier_id   || p.supplierId;
  const supplierName = p.supplier_name || p.supplierName;
  const pan          = (p.pan_number   || p.panNumber || '').toString().trim().toUpperCase();
  const panStatus    = normalizeKey(p.pan_status || p.panStatus);
  const hasCert      = Boolean(p.has_lower_deduction_certificate || p.hasLowerDeductionCertificate);
  const certFrom     = p.certificate_valid_from || p.certificateValidFrom;
  const certTo       = p.certificate_valid_to   || p.certificateValidTo;

  if (!supplierId) {
    pushErr(errors, 'supplier_id', 'SUPPLIER_REQUIRED', 'Supplier is required.');
  }
  if (!supplierName) {
    pushErr(errors, 'supplier_name', 'SUPPLIER_NAME_REQUIRED', 'Supplier name is required.');
  }
  if (pan && !PAN_RE.test(pan)) {
    pushErr(errors, 'pan_number', 'PAN_INVALID', 'PAN must match the 10-character AAAPL1234C shape.');
  }
  if (panStatus === 'AVAILABLE' && !pan) {
    pushErr(errors, 'pan_number', 'PAN_REQUIRED', 'PAN is required when status is AVAILABLE.');
  }
  if (hasCert) {
    if (certFrom && certTo) {
      const a = new Date(certFrom).getTime();
      const b = new Date(certTo).getTime();
      if (Number.isFinite(a) && Number.isFinite(b) && b < a) {
        pushErr(errors, 'certificate_valid_to', 'CERTIFICATE_DATES_INVALID', 'Certificate valid-to cannot be earlier than valid-from.');
      }
    }
  }
  return { ok: errors.length === 0, errors };
}

export function validateTdsDeduction(payload) {
  const errors = [];
  const p = isPlainObjectish(payload) ? payload : {};
  const supplierId = p.supplier_id || p.supplierId;
  const base       = toNumber(p.taxable_base_amount !== undefined ? p.taxable_base_amount : p.taxableBaseAmount);
  const deducted   = toNumber(p.deduction_amount    !== undefined ? p.deduction_amount    : p.deductionAmount);
  const netRaw     = p.net_payable_amount !== undefined ? p.net_payable_amount : p.netPayableAmount;

  if (!supplierId) {
    pushErr(errors, 'supplier_id', 'SUPPLIER_REQUIRED', 'Supplier is required.');
  }
  if (!Number.isFinite(base) || base <= 0) {
    pushErr(errors, 'taxable_base_amount', 'TAXABLE_BASE_REQUIRED', 'Taxable base amount must be greater than 0.');
  }
  if (Number.isFinite(deducted) && Number.isFinite(base) && deducted > base + FY_EPSILON) {
    pushErr(errors, 'deduction_amount', 'TDS_EXCEEDS_BASE', 'Deduction amount cannot exceed taxable base.');
  }
  if (netRaw !== null && netRaw !== undefined && netRaw !== '') {
    const n = toNumber(netRaw);
    if (Number.isFinite(n) && n < 0) {
      pushErr(errors, 'net_payable_amount', 'NET_PAYMENT_NEGATIVE', 'Net payable amount cannot be negative.');
    }
  }
  return { ok: errors.length === 0, errors };
}

export function validateTdsChallan(payload) {
  const errors = [];
  const p = isPlainObjectish(payload) ? payload : {};
  const deposited = toNumber(p.total_deposited_amount !== undefined ? p.total_deposited_amount : p.totalDepositedAmount);
  const month     = p.period_month !== undefined ? p.period_month : p.periodMonth;

  if (!Number.isFinite(deposited) || deposited < 0) {
    pushErr(errors, 'total_deposited_amount', 'DEPOSITED_NEGATIVE', 'Total deposited amount cannot be negative.');
  }
  if (month !== undefined && month !== null && month !== '') {
    const m = Number(month);
    if (!Number.isFinite(m) || m < 1 || m > 12) {
      pushErr(errors, 'period_month', 'PERIOD_MONTH_INVALID', 'Period month must be 1..12.');
    }
  }
  return { ok: errors.length === 0, errors };
}


// ─────────────────────────────────────────────────────────────────
// summarizeTdsPreview — turns a backend preview reply into a thin
// human-readable summary for the form footer.
// ─────────────────────────────────────────────────────────────────

const REASON_LABELS = {
  NO_ACTIVE_RULE:           'No active TDS/TCS rule for this section + date.',
  THRESHOLD_NOT_CROSSED:    'Threshold not yet crossed.',
  SUPPLIER_PROFILE_MISSING: 'Supplier tax profile is missing.',
  CERTIFICATE_EXPIRED:      'Lower-deduction certificate is expired.',
  TAX_EXEMPT:               'Supplier is tax-exempt.'
};

export function summarizeTdsPreview(preview) {
  if (!isPlainObjectish(preview)) {
    return { applies: false, reason: null, reasonLabel: '', summary: 'No TDS/TCS preview available.' };
  }
  const applies = preview.applies === true;
  if (!applies) {
    const reason = preview.reason || null;
    const reasonLabel = reason ? (REASON_LABELS[reason] || reason) : '';
    return {
      applies: false,
      reason,
      reasonLabel,
      summary: reasonLabel || 'TDS/TCS does not apply.'
    };
  }
  const taxType        = normalizeKey(preview.tax_type     || preview.taxType);
  const sectionCode    = sectionLabel(preview.section_code || preview.sectionCode);
  const ratePercent    = toNumber(preview.rate_percent     !== undefined ? preview.rate_percent     : preview.ratePercent);
  const taxableBase    = toNumber(preview.taxable_base_amount !== undefined ? preview.taxable_base_amount : preview.taxableBaseAmount);
  const deductionAmt   = toNumber(preview.deduction_amount   !== undefined ? preview.deduction_amount   : preview.deductionAmount);
  const netPayable     = toNumber(preview.net_payable_amount !== undefined ? preview.net_payable_amount : preview.netPayableAmount);
  const certApplied    = preview.certificate_applied === true || preview.certificateApplied === true;

  let summary = `${taxType} (${sectionCode}) @ ${ratePercent}% on ${formatMoney(taxableBase)} → ${formatMoney(deductionAmt)} deducted (net ${formatMoney(netPayable)})`;
  if (certApplied) summary += ' · Lower-deduction certificate applied.';

  return {
    applies:          true,
    taxType,
    sectionCode,
    ratePercent,
    taxableBaseAmount: taxableBase,
    deductionAmount:   deductionAmt,
    netPayableAmount:  netPayable,
    certificateApplied: certApplied,
    summary
  };
}


// ─────────────────────────────────────────────────────────────────
// Sanity references — keep the importer pattern at the top of this
// module honest. Touching these here keeps a future refactor from
// silently dropping an export.
// ─────────────────────────────────────────────────────────────────

void TAX_TYPES;
void DEDUCTION_STATUSES;
void CHALLAN_STATUSES;
void CERTIFICATE_STATUSES;
void CHALLAN_HEADER_STATUSES;
void SOURCE_TYPES;
void SECTION_CODES_KNOWN;
void PERMS;
